/**
 * The radar's two layers, and what each one costs to see.
 *
 * The radar is one frame over two independent modules, so it cannot be gated by
 * a single `requiredPlugin` the way every other workspace card is. Each mode
 * needs BOTH halves of the decision:
 *
 *   permission  — may this user see it            (role/access-object grant)
 *   plugin      — has this company bought it      (effectivePlugins entitlement)
 *
 * Permission alone was the old rule, and it leaked: system roles are seeded from
 * the whole COMPANY access catalog rather than from the company's plugin list,
 * so a user at a company with no shipments module still holds
 * `shipment:shipment:read` and still got the Shipment Radar — a map of an empty
 * dataset for a module they had not bought. Plugin alone would be just as wrong,
 * since it ignores the user.
 */

export type RadarMode = 'shipment' | 'global';

export interface RadarModeSpec {
  /** Access-object code the user must hold. */
  permission: string;
  /** Backend plugin id the company must be entitled to. */
  plugin: string;
  /** Tab label. */
  label: string;
}

/** Declaration order is preference order: the first available mode is the default. */
export const RADAR_MODES: Readonly<Record<RadarMode, RadarModeSpec>> = {
  shipment: {
    permission: 'shipment:shipment:read',
    plugin: 'shipments',
    label: 'Shipment Radar',
  },
  global: {
    permission: 'global_insights:news:read',
    plugin: 'global-insights',
    label: 'Global Radar',
  },
};

export const RADAR_MODE_ORDER: readonly RadarMode[] = ['shipment', 'global'];

/**
 * The modes a given viewer may actually open, in preference order.
 *
 * Empty means the radar renders nothing at all — the caller should drop the
 * card rather than show an empty frame.
 */
export const resolveAvailableRadarModes = (
  hasPermission: (permission: string) => boolean,
  hasPlugin: (pluginId: string) => boolean
): RadarMode[] =>
  RADAR_MODE_ORDER.filter(
    (mode) => hasPermission(RADAR_MODES[mode].permission) && hasPlugin(RADAR_MODES[mode].plugin)
  );
